/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop */
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { willDestroy } from '@ember/render-modifiers';
import { LinkTo } from '@ember/routing';
import { cancel, debounce, type Timer as EmberRunTimer } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import { and } from 'ember-truth-helpers';
import type { Hotel } from 'frontend-utils/models';
import HotelAndMarketRatesTablesCellRateChanges from 'lumen/components/hotel-and-market/rates/tables/cell-rate-changes';
import type { RatesParams } from 'lumen/components/market/rates/tables/table-market-rates';
import MarketRatesTooltipsTooltipMarketratesTable from 'lumen/components/market/rates/tooltips/tooltip-marketrates-table';
import type { Rate, Ratesummary } from 'lumen/models';
import { RATESUMMARY } from 'lumen/models/ratesummary';
import { RATE_PARAMS } from 'lumen/utils/constants/general-constants';
import { DETAIL_TABS } from 'lumen/utils/constants/rate-constants';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import isNumeric from 'lumen/utils/numeric/is-numeric';
import {
  mapQueryParamsToAPIParams,
  requestRates,
} from 'lumen/utils/rates-request';
import { resolve } from 'rsvp';

export interface CustomColumn extends CustomEmberTableColumn {
  params: RatesParams;
}

interface MarketRatesTablesCellsTdRateSignature {
  Args: {
    cellData: Ratesummary | Rate;
    column: CustomColumn;
  };
}

const MARKET_RATE_LOWER_LIMIT = 0;
const MARKET_RATE_UPPER_LIMIT = 10;

export default class MarketRatesTablesCellsTdRate extends Component<MarketRatesTablesCellsTdRateSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  @tracked compset = [];
  @tracked debouncedFetch: EmberRunTimer | null = null;
  @tracked isHovered = false;
  @tracked isTooltipLoading = true;
  @tracked rates: Rate[] = [];
  @tracked showTooltip = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetRateDifference() {
    const median = this.ratesummary.valueMedian;
    if (!(isEmpty(median) || this.ratesummaryRate.value <= 0) && median > 0) {
      return (this.ratesummaryRate.value - median) / median;
    }

    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compsetRateDifferenceClass() {
    if (this.compsetRateDifference <= MARKET_RATE_LOWER_LIMIT / 100) {
      return 'text-success-default';
    }
    if (this.compsetRateDifference <= MARKET_RATE_UPPER_LIMIT / 100) {
      return 'text-warning-default';
    }

    return 'text-critical-default';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get date() {
    if (this.period?.fromDate) {
      return this.period?.fromDate.format('YYYY-MM-DD');
    }

    return dayjs.utc().format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCompsetRateDifference() {
    if (
      isNumeric(this.compsetRateDifference) &&
      Math.round(100 * this.compsetRateDifference)
    ) {
      return this.intl.formatNumber(this.compsetRateDifference, {
        style: 'percent',
        signDisplay: 'always',
      });
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    const ratesummary = this.isRatesummary
      ? this.ratesummary
      : (this.rate.ratesummary as Ratesummary);

    return ratesummary.market.id!.split('_')[1];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelinfos() {
    return this.rates
      .map((rate) => rate.hotelinfo)
      .filter(
        (hotelinfo) =>
          hotelinfo.id === this.ratesummary.market.hotelinfoId ||
          hotelinfo.otas.find(
            (ota) => ota.id === this.args.column.params.otaId,
          ) !== undefined,
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMemberOnly() {
    return this.rate.membershiptype > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRatesummary() {
    return this.args.cellData.modelName === RATESUMMARY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    if (this.isRatesummary) {
      return this.ratesummary.period;
    }
    return this.rate.period;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rate() {
    if (this.isRatesummary) {
      return this.ratesummary.rate;
    }
    return this.args.cellData as Rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesummary() {
    return this.args.cellData as Ratesummary;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesummaryRate() {
    return this.ratesummary.rate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateValue() {
    return this.hotelAndMarketService.formatRateObject(this.rate, true, false);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRateChanges() {
    return isComparingRates(
      this.args.column.params?.compareType,
      this.args.column.params?.compareValue,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showRefundableIcon() {
    return (
      this.args.column.params.rateParam !== RATE_PARAMS.BEST_FLEX &&
      this.rate.isBestFlexRate
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCompset() {
    const { hotelId } = this.ratesummary.market;
    const hotel = this.store.peekRecord<Hotel>('hotel', hotelId!);
    if (!hotel || hotel.compsets?.length === 0) {
      return this.store.query<Hotel>('hotel', { id: hotelId });
    }
    return resolve();
  }

  getRates(): void {
    const startDate = this.period?.fromDate.format('YYYY-MM-DD');

    const apiParams = mapQueryParamsToAPIParams(
      this.args.column.params,
      startDate,
      startDate,
    );
    const ratesQuery = requestRates(
      'rate',
      this.ratesummary.market.hotelId!,
      apiParams,
      this.store,
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ratesQuery.then((content) => {
      this.rates = [...content];
      this.isTooltipLoading = false;
    });
  }

  @action onMouseEnter(): void {
    if (this.isRatesummary) {
      this.isHovered = true;
      const debouncedFetch = debounce(
        this,
        function () {
          if (this.isHovered) {
            if (!this.rates?.length) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              this.getCompset().then(() => {
                this.getRates();
              });
            }
            this.showTooltip = true;
          }
        },
        250,
      );
      this.debouncedFetch = debouncedFetch;
    }
  }

  @action onMouseLeave(): void {
    this.showTooltip = false;
    this.isHovered = false;

    if (this.debouncedFetch) {
      cancel(this.debouncedFetch);
    }
  }

  @action willDestroyCellElement(): void {
    if (this.debouncedFetch) {
      cancel(this.debouncedFetch);
    }
  }

  <template>
    <div
      class="cell-wrapper flex gap-100 truncate"
      {{willDestroy this.willDestroyCellElement}}
    >
      <div
        class="flex items-center gap-100 truncate"
        {{on "mouseenter" this.onMouseEnter}}
        {{on "mouseleave" this.onMouseLeave}}
        data-test-id="rate-cell-{{this.date}}"
      >
        {{#if this.isRatesummary}}
          <LinkTo
            @query={{hash
              date=this.date
              hotelId=this.hotelId
              type=DETAIL_TABS.RATE_EVOLUTION
            }}
            @route="market.marketrates.detail"
            class="cell-rates__link truncate"
          >
            {{this.rateValue}}
          </LinkTo>
        {{else}}
          <LinkTo
            @query={{hash
              date=this.date
              hotelId=this.hotelId
              type=DETAIL_TABS.RATE_EVOLUTION
            }}
            @route="market.marketrates.detail"
            class="cell-rates__link truncate"
          >
            {{this.rateValue}}
          </LinkTo>
        {{/if}}

        {{#unless this.showRateChanges}}
          {{#if this.isMemberOnly}}
            <div class="cell-rate-changes__prism-icon">
              <PrismIcon @name="user" @size="extra-small" />
            </div>
          {{else if this.showRefundableIcon}}
            <div class="cell-rate-changes__prism-icon">
              <PrismIcon @name="rates-best-flex" @size="extra-small" />
            </div>
          {{/if}}
        {{/unless}}

        {{#if (and this.isHovered this.isRatesummary)}}
          <EmberTooltip
            @isShown={{this.showTooltip}}
            @tooltipClass="base-tooltip rates-calendar-tooltip"
            @updateFor={{this.isTooltipLoading}}
          >
            <MarketRatesTooltipsTooltipMarketratesTable
              @competitorsForCurrentHotelAndOTA={{this.hotelinfos}}
              @isLoading={{this.isTooltipLoading}}
              @otaId={{@column.params.otaId}}
              @ownHotelId={{this.ratesummary.market.hotelinfoId}}
              @params={{@column.params}}
              @period={{this.period}}
              @rates={{this.rates}}
            />
          </EmberTooltip>
        {{/if}}
      </div>

      {{#if this.showRateChanges}}
        {{! @glint-expect-error: Incorrect type }}
        <HotelAndMarketRatesTablesCellRateChanges
          @params={{@column.params}}
          @rateObject={{this.rate}}
        />
      {{else if this.compsetRateDifference}}
        <div
          class="flex items-center truncate {{this.compsetRateDifferenceClass}}"
        >
          {{this.formattedCompsetRateDifference}}
        </div>
      {{/if}}
    </div>
  </template>
}
